/**
 * Questo file definisce un client di base per effettuare chiamate API.
 * - Centralizza la logica di fetch per tutte le richieste HTTP.
 * - Gestisce la configurazione di default, come l'URL di base e gli header.
 * - Standardizza la gestione delle risposte di successo e degli errori.
 * - Semplifica l'invio di dati in formato JSON.
 */

// URL di base per tutte le chiamate API verso il backend.
// In un'applicazione di produzione, questo verrebbe da una variabile d'ambiente.
const API_BASE_URL = '/api';

/**
 * Funzione client per eseguire chiamate API.
 * - Esegue una richiesta HTTP verso un endpoint specifico.
 * - Determina automaticamente il metodo HTTP (GET o POST) in base alla presenza di un body.
 * - Gestisce la serializzazione del body in JSON.
 * - Controlla la risposta e solleva un errore in caso di status non 'ok' (es. 404, 500).
 * - Restituisce la risposta JSON parsata in caso di successo.
 *
 * @param {string} endpoint - Il percorso dell'API da chiamare (es. 'auth/login.php').
 * @param {object} options - Un oggetto di configurazione per la fetch, che può includere 'body' e altri parametri.
 * @returns {Promise<any>} - Una Promise che risolve con i dati JSON della risposta.
 * @throws {Error} - Solleva un errore se la chiamata di rete fallisce o se la risposta ha uno status di errore.
 */
export const fetchClient = async (endpoint, { body, ...customConfig } = {}) => {
    // Definisce gli header di default per le richieste.
    // Specifica che il contenuto inviato e accettato è in formato JSON.
    const headers = { 'Content-Type': 'application/json' };

    // Costruisce l'oggetto di configurazione finale per la chiamata fetch.
    const config = {
        // Se viene fornito un 'body', il metodo è 'POST', altrimenti 'GET'.
        method: body ? 'POST' : 'GET',
        // Unisce eventuali configurazioni personalizzate passate come argomento.
        ...customConfig,
        // Unisce gli header di default con eventuali header personalizzati.
        headers: {
            ...headers,
            ...customConfig.headers,
        },
    };

    // Se è presente un body, lo converte in una stringa JSON.
    // Questo è necessario per inviare dati tramite richieste POST/PUT.
    if (body) {
        config.body = JSON.stringify(body);
    }

    try {
        // Esegue la richiesta HTTP usando l'URL completo e la configurazione creata.
        const response = await fetch(`${API_BASE_URL}/${endpoint}`, config);

        // Controlla se la risposta del server indica un errore (es. status 4xx o 5xx).
        if (!response.ok) {
            // Tenta di leggere il corpo della risposta per ottenere un messaggio di errore JSON.
            // Se fallisce (es. risposta vuota), usa un oggetto vuoto.
            const errorData = await response.json().catch(() => ({}));
            // Solleva un nuovo errore, usando il messaggio fornito dal server o un messaggio generico.
            throw new Error(errorData.error || 'Errore di rete o del server.');
        }

        // Se lo status della risposta è 204 (No Content), non c'è corpo da parsare.
        // Termina la funzione senza restituire dati.
        if (response.status === 204) {
            return;
        }

        // Se la risposta è valida (status 2xx), la parse come JSON e la restituisce.
        return await response.json();
    } catch (error) {
        // Se si verifica un qualsiasi errore durante il processo (rete, parsing, ecc.),
        // lo rilancia per permettere al codice chiamante (es. un thunk Redux) di gestirlo.
        console.error("Errore nel fetchClient:", error);
        throw error;
    }
};